import { createSelector } from 'reselect'
import { groupBy, sortBy } from 'lodash'

import { outOfOrderSearch } from '../helpers/outOfOrderSearch'
import { beatsByPosition } from '../helpers/beats'

import { allCardsSelector } from './cardsFirstOrder'
import {
  beatsByBookSelector,
  currentTimelineSelector,
  outlineFilterSelector,
  outlineSearchTermSelector,
} from './secondOrder'
import { allLinesSelector } from './linesFirstOrder'
import { sortCardsInBeat } from '../helpers/cards'
import { allBeatsAsArraySelector } from './beatsFirstOrder'
import { allBookIdsSelector } from './booksFirstOrder'
import { makeBeatTitleSelector } from './timelineThirdOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Beat} Beat
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Line} Line
 */

/**
 * @param {String} lineAttr
 * @param {String} beatAttr
 * @param {Map<number, boolean>} collapsedBeats
 * @param {Record<number, number>} beatPositions
 * @return {function(Record<String, Array<Card>>, Card): Record<String, Array<Card>>}
 */
function cardReduce(lineAttr, beatAttr, collapsedBeats, beatPositions) {
  return (acc, card) => {
    const cardBeatId = card[beatAttr]
    const beatId = (collapsedBeats && collapsedBeats.get(cardBeatId)) || cardBeatId
    const val = acc[`${card[lineAttr]}-${beatId}`]
    if (val && val.length) {
      const cards = [...val, card]
      const sortedCards = sortBy(sortBy(cards, 'positionWithinLine'), (c) => {
        const bId = c[beatAttr]
        return beatPositions[bId]
      })
      acc[`${card[lineAttr]}-${beatId}`] = sortedCards
    } else {
      acc[`${card[lineAttr]}-${beatId}`] = [card]
    }

    return acc
  }
}

/**
 * @type {function(PlottrState): Array<Beat>}
 */
const sortedBeatsByBookSelector = createSelector(
  beatsByBookSelector,
  beatsByPosition(() => true)
)

/**
 * @type {function(PlottrState): Record<number, String>}
 */
const stringifiedCardsByIdWhenSearchedSelector = createSelector(
  allCardsSelector,
  outlineSearchTermSelector,
  (cards, searchTerm) => {
    if (searchTerm && typeof searchTerm === 'string' && searchTerm !== '') {
      return cards.reduce((acc, nextCard) => {
        return {
          ...acc,
          [nextCard.id]: JSON.stringify(nextCard).toLowerCase(),
        }
      }, {})
    } else {
      return {}
    }
  }
)

/**
 * @type {function(PlottrState): Array<Card>}
 */
export const visibleOutlineCardsSelector = createSelector(
  allCardsSelector,
  outlineFilterSelector,
  (cards, outlineFilter) => {
    return outlineFilter?.length ? cards.filter((c) => outlineFilter.includes(c.lineId)) : cards
  }
)

/**
 * @type {function(PlottrState): Array<Line>}
 */
export const linesByBookSelector = createSelector(
  allLinesSelector,
  currentTimelineSelector,
  (lines, bookId) => {
    return lines.filter((l) => l && l.bookId == bookId)
  }
)

/**
 * @type {function(PlottrState): Array<Line>}
 */
const sortedLinesByBookSelector = createSelector(linesByBookSelector, (lines) =>
  sortBy(lines, 'position')
)

/**
 * @type {function(PlottrState): Array<Card>}
 */
export const outlineSearchedCardsSelector = createSelector(
  visibleOutlineCardsSelector,
  sortedBeatsByBookSelector,
  outlineSearchTermSelector,
  stringifiedCardsByIdWhenSearchedSelector,
  (cards, allSortedBeats, outlineSearchTerm, stringifiedCards) => {
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    const lowerCaseSearchTerms =
      outlineSearchTerm &&
      outlineSearchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)
    return lowerCaseSearchTerms
      ? cards.filter(({ id }) => {
          return outOfOrderSearch(lowerCaseSearchTerms, stringifiedCards[id])
        })
      : cards
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<Card>>}
 */
export const outlineSearchedCardMapSelector = createSelector(
  visibleOutlineCardsSelector,
  sortedBeatsByBookSelector,
  outlineSearchTermSelector,
  stringifiedCardsByIdWhenSearchedSelector,
  (cards, allSortedBeats, outlineSearchTerm, stringifiedCards) => {
    const beatIds = allSortedBeats.map(({ id }) => id)
    const beatPositions = beatIds.map((x) => x)
    beatIds.forEach((beatId, index) => (beatPositions[beatId] = index))
    const lowerCaseSearchTerms =
      outlineSearchTerm &&
      outlineSearchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)
    const filteredCards = lowerCaseSearchTerms
      ? cards.filter(({ id }) => {
          return outOfOrderSearch(lowerCaseSearchTerms, stringifiedCards[id])
        })
      : cards
    return filteredCards.reduce(cardReduce('lineId', 'beatId', new Map(), beatPositions), {})
  }
)

/**
 * @type {function(PlottrState): Record<number, Array<Card>>}
 */
export const allCardsByBeatSelector = createSelector(allCardsSelector, (cards) => {
  return groupBy(
    cards.map((c) => ({
      ...c,
      // NOTE(ed): `positionInChapter` comes from the prehistoric
      // versions of Plottr where there was a distinction in data type
      // between beats and chapters even though they were the same
      // thing in the system.  Even though they're different things
      // from a writer's perspective, we decided to make them the same
      // thing in the system to reduce code-duplication.
      //
      // FIXME(ed): We might be able to remove `positionInChapter`
      positionInBeat: c.positionInBeat
        ? c.positionInBeat
        : 'positionInChapter' in c && typeof c.positionInChapter === 'number'
          ? c.positionInChapter
          : 0,
      beatId: c.beatId,
    })),
    'beatId'
  )
})

const bookIdSelector = (_state, bookId) => bookId

/**
 * @type {function(PlottrState): Record<number, Array<Card>>}
 */
export const allCardsByBeatForBookSelector = createSelector(
  allCardsSelector,
  bookIdSelector,
  allBeatsAsArraySelector,
  allBookIdsSelector,
  makeBeatTitleSelector,
  (cards, currentBookId, beats, bookIds) => {
    const beatIds = beats
      .filter(({ bookId }) => (currentBookId ? currentBookId == bookId : bookId == bookIds[0]))
      .map(({ id }) => id)
    return groupBy(
      cards
        .filter((card) => beatIds.includes(card.beatId))
        .map((c) => ({
          ...c,
          // NOTE(ed): see note in `allCardsByBeatSelector`.
          positionInBeat: c.positionInBeat
            ? c.positionInBeat
            : 'positionInChapter' in c && typeof c.positionInChapter === 'number'
              ? c.positionInChapter
              : 0,
          beatId: c.beatId,
        })),
      'beatId'
    )
  }
)

/**
 * @param {PlottrState} _state
 * @param {Beat} beat
 * @return {Beat}
 */
const beatSelector = (_state, beat) => beat
/**
 * @type {function(PlottrState, Beat): Array<Card>}
 */
export const allCardsInBeatSelector = createSelector(
  allCardsByBeatSelector,
  beatSelector,
  sortedLinesByBookSelector,
  (cards, beat, sortedLines) => {
    const cardsInBeat = cards[beat.id] ?? []
    return sortCardsInBeat(beat.autoOutlineSort, cardsInBeat, sortedLines)
  }
)

/**
 * @type {function(PlottrState, Beat): Array<Card>}
 */
export const visibleCardsInBeatSelector = createSelector(
  outlineSearchedCardsSelector,
  beatSelector,
  sortedLinesByBookSelector,
  (cards, beat, sortedLines) => {
    const cardsInBeat = cards.filter((c) => c.beatId == beat.id)
    return sortCardsInBeat(beat.autoOutlineSort, cardsInBeat, sortedLines)
  }
)
